// @ts-nocheck
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  FileText,
  Award,
  AlertTriangle,
  BarChart3,
  Activity,
} from 'lucide-react';
import { analyticsApi } from '@/api/endpoints';
import { Card, CardHeader, Badge, StatCard } from '@azyntor-labs/nexus-ui';
import { useNavigate } from 'react-router-dom';

type TimeRange = '7d' | '30d' | '90d' | 'all';

// Convert time range label to number of days for the API
const timeRangeToDays = (range: TimeRange): number | undefined => {
  switch (range) {
    case '7d': return 7;
    case '30d': return 30;
    case '90d': return 90;
    case 'all': return undefined; // no filter
  }
};

export const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  const days = timeRangeToDays(timeRange);

  const { data: dashboardResponse, isLoading } = useQuery({
    queryKey: ['analytics-dashboard', timeRange],
    queryFn: () => analyticsApi.getDashboard(days ? { days } : undefined),
  });

  const { data: examsResponse } = useQuery({
    queryKey: ['analytics-exams', timeRange],
    queryFn: () => analyticsApi.getExamsByTenant(days ? { days } : undefined),
  });

  const { data: difficultQuestionsResponse } = useQuery({
    queryKey: ['analytics-difficult-questions', timeRange],
    queryFn: () => analyticsApi.getDifficultQuestions({ limit: 10, threshold: 50, ...(days ? { days } : {}) }),
  });

  const { data: topStudentsResponse } = useQuery({
    queryKey: ['analytics-top-students', timeRange],
    queryFn: () => analyticsApi.getTopStudents({ limit: 10, ...(days ? { days } : {}) }),
  });

  const { data: violationsResponse } = useQuery({
    queryKey: ['analytics-violations', timeRange],
    queryFn: () => analyticsApi.getViolationAnalytics(days ? { days } : undefined),
  });

  const { data: recentViolationsResponse } = useQuery({
    queryKey: ['analytics-recent-violations', timeRange],
    queryFn: () => analyticsApi.getRecentViolations({ limit: 10 }),
  });

  const recentViolations = recentViolationsResponse?.data?.data || [];

  const dashboardData = dashboardResponse?.data.data;
  const stats = dashboardData?.overview;
  const exams = examsResponse?.data.data?.exams || [];
  const difficultQuestions = difficultQuestionsResponse?.data.data || [];
  const topStudents = topStudentsResponse?.data.data || [];
  const violationsRaw: any[] = Array.isArray(violationsResponse?.data.data) ? violationsResponse.data.data : [];
  // Build a lookup map from violation_type -> total_count for easy access
  const violations = violationsRaw.length > 0
    ? violationsRaw.reduce((acc: Record<string, number>, v: any) => {
        if (v.violation_type) acc[v.violation_type] = Number(v.total_count ?? 0);
        return acc;
      }, {})
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-white/60">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Analytics Dashboard</h1>
          <p className="text-white/50 mt-1">
            Comprehensive insights into exam performance and student analytics
          </p>
        </div>

        {/* Time range selector */}
        <div className="flex items-center space-x-1 bg-white/5 border border-white/[0.06] rounded-lg p-1">
          {[
            { value: '7d', label: '7 Days' },
            { value: '30d', label: '30 Days' },
            { value: '90d', label: '90 Days' },
            { value: 'all', label: 'All Time' },
          ].map((range) => (
            <button
              key={range.value}
              onClick={() => setTimeRange(range.value as TimeRange)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                timeRange === range.value
                  ? 'bg-teal-500 text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Students"
          value={stats?.total_students || 0}
          icon={<Users />}
          color="teal"
          subtitle="Registered"
        />
        <StatCard
          title="Total Exams"
          value={stats?.total_exams || 0}
          icon={<FileText />}
          color="purple"
          subtitle={`${stats?.active_exams || 0} active`}
        />
        <StatCard
          title="Average Pass Rate"
          value={`${stats?.average_pass_rate?.toFixed(1) || 0}%`}
          icon={<Award />}
          color={stats?.average_pass_rate >= 70 ? 'green' : 'orange'}
        />
        <StatCard
          title="Total Attempts"
          value={stats?.total_attempts || 0}
          icon={<Activity />}
          color="teal"
          subtitle="Exam submissions"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pass Rate Chart */}
        <Card>
          <CardHeader
            title="Pass Rate Overview"
            subtitle="Overall exam performance"
          />
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-48 h-48">
              {/* Donut chart using conic-gradient */}
              <div
                className="w-full h-full rounded-full"
                style={{
                  background: `conic-gradient(
                    #10b981 0deg ${(stats?.average_pass_rate || 0) * 3.6}deg,
                    #ef4444 ${(stats?.average_pass_rate || 0) * 3.6}deg 360deg
                  )`,
                }}
              />
              <div className="absolute inset-4 bg-[#12121a] rounded-full flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">
                    {stats?.average_pass_rate?.toFixed(0) || 0}%
                  </div>
                  <div className="text-sm text-white/50">Pass Rate</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <div className="flex items-center justify-center space-x-2 mb-1">
                <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                <span className="text-sm font-medium text-white/70">Passed</span>
              </div>
              <div className="text-2xl font-bold text-emerald-400">
                {stats?.average_pass_rate?.toFixed(0) || 0}%
              </div>
            </div>
            <div className="text-center p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center justify-center space-x-2 mb-1">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <span className="text-sm font-medium text-white/70">Failed</span>
              </div>
              <div className="text-2xl font-bold text-red-400">
                {(100 - (stats?.average_pass_rate || 0)).toFixed(0)}%
              </div>
            </div>
          </div>
        </Card>

        {/* Exam Activity Chart */}
        <Card>
          <CardHeader
            title="Exam Activity"
            subtitle="Key metrics overview"
          />
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/70">Total Attempts</span>
                <span className="text-sm font-semibold text-white">
                  {stats?.total_attempts || 0}
                </span>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/70">Active Exams</span>
                <span className="text-sm font-semibold text-white">
                  {stats?.active_exams || 0} / {stats?.total_exams || 0}
                </span>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
                  style={{
                    width: `${
                      ((stats?.active_exams || 0) / Math.max(stats?.total_exams || 1, 1)) * 100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/70">Average Score</span>
                <span className="text-sm font-semibold text-white">
                  {stats?.average_pass_rate?.toFixed(1) || 0}%
                </span>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                  style={{ width: `${stats?.average_pass_rate || 0}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/70">Student Engagement</span>
                <span className="text-sm font-semibold text-white">
                  {stats?.total_students || 0} students
                </span>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
                  style={{ width: `${Math.min(100, (stats?.total_students || 0) * 2)}%` }}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Exams */}
        <Card>
          <CardHeader
            title="Top Performing Exams"
            subtitle="By average score"
          />
          {exams.length > 0 ? (
            <div className="space-y-3">
              {exams.slice(0, 5).map((exam, index) => (
                <div
                  key={exam.exam_id}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      index === 0 ? 'bg-amber-500/20 text-amber-400' :
                      index === 1 ? 'bg-white/10 text-white/70' :
                      index === 2 ? 'bg-orange-500/20 text-orange-400' :
                      'bg-teal-500/15 text-teal-400'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-white">{exam.exam_title}</div>
                      <div className="text-sm text-white/50">
                        {exam.total_attempts} attempts
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-white">
                      {exam.average_score?.toFixed(1)}%
                    </div>
                    <Badge
                      variant={exam.pass_rate >= 70 ? 'success' : exam.pass_rate >= 50 ? 'warning' : 'danger'}
                      size="sm"
                    >
                      {exam.pass_rate?.toFixed(0)}% pass
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40">No exam data available</p>
            </div>
          )}
        </Card>

        {/* Top Students */}
        <Card>
          <CardHeader
            title="Top Students"
            subtitle="By performance"
          />
          {topStudents.length > 0 ? (
            <div className="space-y-3">
              {topStudents.slice(0, 5).map((student: any, index: number) => (
                <div
                  key={student.student_id || index}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      index === 0 ? 'bg-amber-500/20 text-amber-400' :
                      index === 1 ? 'bg-white/10 text-white/70' :
                      index === 2 ? 'bg-orange-500/20 text-orange-400' :
                      'bg-cyan-500/15 text-cyan-400'
                    }`}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-white">
                        {student.student_name || 'Student ' + (index + 1)}
                      </div>
                      <div className="text-sm text-white/50">
                        {student.total_exams_taken || 0} exams
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-white">
                      {student.average_score?.toFixed(1) || 0}%
                    </div>
                    <span className="text-sm text-emerald-400">
                      {student.pass_rate?.toFixed(0) || 0}% pass
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40">No student data available</p>
            </div>
          )}
        </Card>
      </div>

      {/* Difficult Questions */}
      <Card>
        <CardHeader
          title="Most Challenging Questions"
          subtitle="Questions with low success rates"
        />
        {difficultQuestions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">
                    Question
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">
                    Times Used
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">
                    Accuracy
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">
                    Avg Time
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">
                    Difficulty
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {difficultQuestions.map((q: any, index: number) => (
                  <tr key={q.question_id || index} className="hover:bg-white/[0.02]">
                    <td className="py-3 px-4">
                      <div className="text-sm text-white/80 truncate max-w-xs">
                        {q.question_text || 'Question ' + (index + 1)}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-white/60">
                      {q.total_attempts || 0}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-24 bg-white/10 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              (q.success_rate || 0) >= 70
                                ? 'bg-emerald-500'
                                : (q.success_rate || 0) >= 50
                                ? 'bg-amber-500'
                                : 'bg-red-500'
                            }`}
                            style={{ width: `${q.success_rate || 0}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-white">
                          {q.success_rate?.toFixed(0) || 0}%
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-white/60">
                      {q.average_time_seconds
                        ? `${Math.floor(q.average_time_seconds / 60)}:${(
                            q.average_time_seconds % 60
                          )
                            .toString()
                            .padStart(2, '0')}`
                        : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <Badge
                        variant={
                          q.difficulty === 'hard'
                            ? 'danger'
                            : q.difficulty === 'medium'
                            ? 'warning'
                            : 'success'
                        }
                        size="sm"
                      >
                        {q.difficulty || 'medium'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <BarChart3 className="w-12 h-12 text-white/20 mx-auto mb-2" />
            <p className="text-white/40">No question analytics available</p>
          </div>
        )}
      </Card>

      {/* Analytics Navigation Tabs */}
      <div className="flex items-center space-x-1 bg-white/5 border border-white/[0.06] rounded-lg p-1">
        <button className="px-4 py-2 rounded-lg text-sm font-medium bg-teal-500 text-white">
          Standard
        </button>
        <button
          onClick={() => navigate('/analytics/enhanced')}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5"
        >
          Enhanced
        </button>
        <button
          onClick={() => navigate('/analytics/campaigns')}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5"
        >
          Campaigns
        </button>
      </div>

      {/* Recent Violations */}
      {recentViolations.length > 0 && (
        <Card>
          <CardHeader
            title="Recent Violations"
            subtitle="Latest proctoring violations across all exams"
          />
          <div className="space-y-3">
            {recentViolations.slice(0, 5).map((v: any, idx: number) => (
              <div
                key={v.id || idx}
                className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]"
              >
                <div className="flex items-center space-x-3">
                  <AlertTriangle className={`w-4 h-4 ${
                    v.severity === 'high' || v.severity === 'critical' ? 'text-red-400' : 'text-amber-400'
                  }`} />
                  <div>
                    <div className="text-sm font-medium text-white">{v.violation_type || v.type}</div>
                    <div className="text-xs text-white/50">{v.student_name || 'Student'} - {v.exam_title || 'Exam'}</div>
                  </div>
                </div>
                <div className="text-xs text-white/40">
                  {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Violations Analysis */}
      {violations && Object.keys(violations).length > 0 && (
        <Card>
          <CardHeader
            title="Proctoring Violations"
            subtitle="Violation breakdown by type"
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(violations)
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .map(([type, count]) => {
                const colorMap: Record<string, { bg: string; border: string; icon: string; text: string }> = {
                  tab_switch:           { bg: 'bg-red-500/10',    border: 'border-red-500/30',    icon: 'text-red-400',    text: 'text-red-300' },
                  copy_paste:           { bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: 'text-orange-400', text: 'text-orange-300' },
                  no_face:              { bg: 'bg-amber-500/10',  border: 'border-amber-500/30',  icon: 'text-amber-400',  text: 'text-amber-300' },
                  multiple_faces:       { bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: 'text-purple-400', text: 'text-purple-300' },
                  looking_away:         { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: 'text-yellow-400', text: 'text-yellow-300' },
                  mobile_detected:      { bg: 'bg-pink-500/10',   border: 'border-pink-500/30',   icon: 'text-pink-400',   text: 'text-pink-300' },
                  prohibited_object:    { bg: 'bg-rose-500/10',   border: 'border-rose-500/30',   icon: 'text-rose-400',   text: 'text-rose-300' },
                  devtools_open:        { bg: 'bg-sky-500/10',    border: 'border-sky-500/30',    icon: 'text-sky-400',    text: 'text-sky-300' },
                  fullscreen_exit:      { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', icon: 'text-indigo-400', text: 'text-indigo-300' },
                  gaze_anomaly:         { bg: 'bg-teal-500/10',   border: 'border-teal-500/30',   icon: 'text-teal-400',   text: 'text-teal-300' },
                  ai_assistant_detected:{ bg: 'bg-fuchsia-500/10',border: 'border-fuchsia-500/30',icon: 'text-fuchsia-400',text: 'text-fuchsia-300' },
                };
                const fallback = { bg: 'bg-slate-500/10', border: 'border-slate-500/30', icon: 'text-slate-400', text: 'text-slate-300' };
                const colors = colorMap[type] || fallback;
                const label = type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                return (
                  <div key={type} className={`p-4 rounded-lg ${colors.bg} border ${colors.border}`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertTriangle className={`w-5 h-5 ${colors.icon}`} />
                      <span className={`text-sm font-medium ${colors.text}`}>{label}</span>
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {count as number}
                    </div>
                  </div>
                );
              })}
          </div>
        </Card>
      )}
    </div>
  );
};
